import type { ClassDefinition } from "@hd2/data";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import { useMemo, useState } from "react";
import { useAssignedClass } from "../../hooks/use-assigned-class.ts";
import { openClassPicker } from "../../hooks/use-class-picker.ts";
import { useClasses } from "../../hooks/use-classes.ts";
import type { LoadoutSlotKey } from "../../hooks/use-current-loadout.ts";
import { useDossierClass } from "../../hooks/use-dossier-class.ts";
import type { UseItemsResult } from "../../hooks/use-items.ts";
import { isEffectivelyOwned } from "../dossier/loadout/loadout-ownership.ts";
import { SLOT_TO_LOADOUT_KEY } from "../dossier/loadout/loadout-slot-match.ts";
import {
	getValidItems,
	type PickerItem,
	slugify,
} from "./loadout-picker-items.ts";

function getClassAllowedSlugs(
	cls: ClassDefinition,
	slotKey: LoadoutSlotKey,
	items: UseItemsResult,
): Set<string> {
	if (slotKey === "armor") {
		const passiveSlugs = new Set(Object.keys(cls.loadout.armorPassives ?? {}));
		const allowed = new Set<string>();
		for (const passive of items.passives) {
			if (!passiveSlugs.has(slugify(passive.name))) {
				continue;
			}
			for (const armor of passive.armors) {
				allowed.add(slugify(armor.name));
			}
		}
		return allowed;
	}
	const loadoutKey = SLOT_TO_LOADOUT_KEY[slotKey];
	return new Set(Object.keys(cls.loadout[loadoutKey] ?? {}));
}

export function LoadoutSlotPickerDialog({
	open,
	slotLabel,
	slotKey,
	items,
	ownedItemSlugs,
	excludedItemSlugs,
	ownedWarbondSlugs,
	onSelect,
	onClear,
	onClose,
}: {
	open: boolean;
	slotLabel: string;
	slotKey: LoadoutSlotKey;
	items: UseItemsResult;
	ownedItemSlugs: Set<string>;
	excludedItemSlugs: Set<string>;
	ownedWarbondSlugs: Set<string>;
	onSelect: (slug: string) => void;
	onClear: () => void;
	onClose: () => void;
}) {
	const itemOwned = (item: PickerItem) =>
		isEffectivelyOwned(
			item.slug,
			item.sourceSlug,
			ownedItemSlugs,
			excludedItemSlugs,
			ownedWarbondSlugs,
		);
	const { classes } = useClasses();
	const [assigned] = useAssignedClass();
	const dossierClass = useDossierClass();

	const assignedClass = assigned
		? (classes.find((c) => c.slug === assigned.slug) ?? null)
		: null;

	const filterClass: ClassDefinition | null =
		assignedClass ?? dossierClass ?? null;
	const filterSource: "assigned" | "dossier" | null = assignedClass
		? "assigned"
		: dossierClass
			? "dossier"
			: null;

	const [showAll, setShowAll] = useState(false);
	const [showOutsideClass, setShowOutsideClass] = useState(false);

	const allValid = useMemo(
		() => getValidItems(slotKey, items),
		[slotKey, items],
	);

	const classAllowed = useMemo(
		() =>
			filterClass ? getClassAllowedSlugs(filterClass, slotKey, items) : null,
		[filterClass, slotKey, items],
	);

	const filtered = allValid.filter((item) => {
		if (classAllowed && !showOutsideClass && !classAllowed.has(item.slug)) {
			return false;
		}
		if (!showAll && !itemOwned(item)) {
			return false;
		}
		return true;
	});

	function handleSelect(slug: string) {
		onSelect(slug);
		onClose();
	}

	return (
		<Dialog open={open} onClose={onClose} className="relative z-50">
			<DialogBackdrop className="fixed inset-0 bg-surface/80" />
			<div className="fixed inset-0 flex items-end justify-center md:items-center md:p-4">
				<DialogPanel className="flex max-h-[80vh] w-full flex-col border border-outline-variant bg-surface-container-low shadow-[4px_4px_0_0_rgba(0,0,0,1)] md:max-w-lg">
					<div className="flex items-center justify-between border-b border-outline-variant p-3 md:p-4">
						<DialogTitle className="font-mono text-[10px] text-outline uppercase tracking-[0.2em]">
							{`// SELECT ${slotLabel}`}
						</DialogTitle>
						<button
							type="button"
							onClick={onClose}
							className="inline-grid h-7 w-7 cursor-pointer place-items-center"
						>
							<span className="material-symbols-outlined text-outline text-[18px] leading-none">
								close
							</span>
						</button>
					</div>

					<div className="flex flex-wrap items-center gap-2 border-b border-outline-variant bg-surface-container-lowest px-3 py-2 md:px-4">
						{filterClass ? (
							<button
								type="button"
								onClick={() => setShowOutsideClass(!showOutsideClass)}
								className={`cursor-pointer border-l-4 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-colors ${
									showOutsideClass
										? "bg-surface-container text-outline line-through"
										: "bg-surface-container-high text-on-surface"
								}`}
								style={{
									borderLeftColor: filterClass.themeColorHex,
								}}
								title={
									filterSource === "assigned"
										? "Assigned class filter"
										: "Filtering by dossier class"
								}
							>
								{`${filterSource === "assigned" ? "ASSIGNED" : "DOSSIER"}: ${filterClass.name.toUpperCase()}`}
							</button>
						) : (
							<button
								type="button"
								onClick={openClassPicker}
								className="cursor-pointer border border-primary bg-primary/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-primary transition-colors hover:bg-primary/20"
							>
								{"// CHOOSE A CLASS"}
							</button>
						)}
						<button
							type="button"
							onClick={() => setShowAll(!showAll)}
							className={`cursor-pointer border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-colors ${
								showAll
									? "border-outline text-outline"
									: "border-secondary text-secondary"
							}`}
						>
							{showAll ? "ALL ITEMS" : "OWNED ONLY"}
						</button>
					</div>

					<div className="flex-1 overflow-y-auto">
						<button
							type="button"
							onClick={() => {
								onClear();
								onClose();
							}}
							className="flex w-full cursor-pointer items-center gap-3 border-b border-outline-variant/50 p-3 text-left font-mono text-[10px] uppercase tracking-wide text-outline transition-colors hover:bg-surface-container-high"
						>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center text-outline-variant">
								<span className="material-symbols-outlined text-[16px] leading-none">
									block
								</span>
							</span>
							{"NONE"}
						</button>
						{filtered.map((item) => {
							const outsideClass =
								classAllowed !== null && !classAllowed.has(item.slug);
							return (
								<button
									type="button"
									key={item.slug}
									onClick={() => handleSelect(item.slug)}
									className="flex w-full cursor-pointer items-center gap-3 border-b border-outline-variant/50 p-3 text-left transition-colors hover:bg-surface-container-high"
								>
									<div className="flex h-8 w-8 shrink-0 items-center justify-center">
										{item.iconUrl ? (
											<img
												src={item.iconUrl}
												alt={item.name}
												className="h-8 w-8 object-contain"
											/>
										) : (
											<span className="font-mono text-[8px] text-outline-variant">
												{"//"}
											</span>
										)}
									</div>
									<span className="min-w-0 flex-1 truncate font-mono text-[10px] uppercase tracking-wide text-on-surface">
										{item.name}
									</span>
									{outsideClass && (
										<span className="shrink-0 font-mono text-[8px] text-error/80 uppercase">
											{"// OUT-OF-CLASS"}
										</span>
									)}
									{!itemOwned(item) && !outsideClass && (
										<span className="shrink-0 font-mono text-[8px] text-outline-variant uppercase">
											{"// LOCKED"}
										</span>
									)}
								</button>
							);
						})}
						{filtered.length === 0 && (
							<div className="space-y-2 p-4 text-center">
								<div className="font-mono text-[10px] text-outline-variant uppercase tracking-wide">
									{"// NO ITEMS AVAILABLE"}
								</div>
								{filterClass && !showOutsideClass && (
									<button
										type="button"
										onClick={() => setShowOutsideClass(true)}
										className="cursor-pointer border border-outline px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-outline transition-colors hover:border-primary hover:text-primary"
									>
										{"SHOW OUT-OF-CLASS"}
									</button>
								)}
							</div>
						)}
					</div>
				</DialogPanel>
			</div>
		</Dialog>
	);
}
